import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Check, X, Pill, CalendarClock, FlaskConical, ChevronRight } from "lucide-react";

export default function ApprovalsPage() {
  const { user } = useAuth();
  const isSuper = user?.role === "admin" || user?.role === "organizer";
  const [drugReqs, setDrugReqs] = useState([]);
  const [reschedReqs, setReschedReqs] = useState([]);
  const [pendingRegimens, setPendingRegimens] = useState([]);

  const load = async () => {
    const [d, r, p] = await Promise.all([
      api.get("/drug-requests?status_filter=pending"),
      api.get("/reschedule-requests?status_filter=pending"),
      // Patient-originated reschedule requests come from the mobile PWA's
      // POST /patient/requests endpoint with kind='reschedule_appointment'.
      api.get("/provider/requests?status_filter=pending").catch(() => ({ data: [] })),
    ]);
    setDrugReqs(d.data);
    // Combine legacy reschedule-requests with new patient_requests of kind
    // 'reschedule_appointment' so the Approvals card holds the full pending queue.
    const patientReschedules = (p.data || []).filter((x) => x.kind === "reschedule_appointment");
    const merged = [
      ...r.data.map((x) => ({ ...x, _source: "legacy" })),
      ...patientReschedules.map((x) => ({
        id: x.id, _source: "patient_request",
        patient_id: x.patient_id, patient_name: x.patient_name,
        old_date: x.old_date,
        new_date: x.new_date,
        event_label: x.event_label,
        event_type: x.event_type,
        event_time: x.event_time,
        regimen_name: x.regimen_name,
        cycle_number: x.cycle_number,
        reason: x.message,
        event_id: x.event_id, calendar_id: x.calendar_id,
        created_at: x.created_at,
      })),
    ].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    setReschedReqs(merged);
    if (isSuper) {
      try {
        const reg = await api.get("/regimens/pending");
        setPendingRegimens(reg.data);
      } catch (e) { /* non-super: ignore */ }
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const decide = async (kind, id, decision, row = null) => {
    if (row && row._source === "patient_request") {
      // Patient-originated request — accept via the mobile endpoint so the
      // calendar is auto-shifted and an in-app notification is sent back.
      await api.patch(`/provider/requests/${id}/decision`, {
        decision: decision === "approved" ? "accepted" : "denied",
        apply_change: decision === "approved",
      });
    } else {
      const url = kind === "drug" ? `/drug-requests/${id}/decision` : `/reschedule-requests/${id}/decision`;
      await api.post(url, { decision });
    }
    load();
  };

  const decideRegimen = async (id, decision) => {
    const note = decision === "rejected"
      ? window.prompt("Optional note for the regimen owner (reason for rejection):") || ""
      : "";
    await api.post(`/regimens/${id}/approve`, { decision, note });
    load();
  };

  return (
    <div className="p-8 max-w-[1400px]" data-testid="approvals-page">
      <h1 className="font-display text-3xl font-bold text-stone-900 mb-1">Approval queue</h1>
      <p className="text-stone-500 mb-6">Review pending drug additions, regimen changes, and patient reschedule requests.</p>

      {isSuper && (
        <div className="bg-white border border-stone-200 rounded-xl shadow-soft overflow-hidden mb-6" data-testid="regimen-approvals-card">
          <div className="px-5 py-4 border-b border-stone-100 flex items-center gap-2">
            <FlaskConical className="w-4 h-4 text-brand-500" />
            <h2 className="font-display font-semibold text-stone-900">Regimen requests <span className="text-stone-400 font-normal">({pendingRegimens.length})</span></h2>
            <span className="ml-2 text-xs text-stone-400">— created or edited by a provider, awaiting admin approval</span>
          </div>
          <div className="divide-y divide-stone-100">
            {pendingRegimens.length === 0 && <div className="p-8 text-center text-stone-500 text-sm">No regimens are awaiting approval.</div>}
            {pendingRegimens.map((r) => (
              <div key={r.id} className="px-5 py-4" data-testid={`pending-regimen-${r.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Link to={`/regimens/${r.id}`} className="font-semibold text-stone-900 hover:text-brand-700 truncate" data-testid={`pending-regimen-link-${r.id}`}>
                        {r.name}
                      </Link>
                      <span className="px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 text-xs font-medium">{r.last_approved_snapshot ? "Edit" : "New"}</span>
                      <span className="text-xs text-stone-500">{r.disease_type}</span>
                    </div>
                    <div className="text-xs text-stone-500 mt-0.5">
                      by {r.owner_name} • {r.cycles?.length || 0} cycle{(r.cycles?.length || 0) !== 1 ? "s" : ""} • {new Date(r.updated_at || r.created_at).toLocaleString()}
                    </div>
                    {r.description && <div className="text-sm text-stone-600 mt-1.5 line-clamp-2">{r.description}</div>}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Link to={`/regimens/${r.id}`} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-stone-300 hover:bg-stone-50 text-xs font-medium text-stone-700" data-testid={`pending-regimen-view-${r.id}`}>
                      Review <ChevronRight className="w-3 h-3" />
                    </Link>
                    <button onClick={() => decideRegimen(r.id, "approved")} className="p-2 rounded-lg bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200" title="Approve" data-testid={`approve-regimen-${r.id}`}><Check className="w-4 h-4" /></button>
                    <button onClick={() => decideRegimen(r.id, "rejected")} className="p-2 rounded-lg bg-red-50 hover:bg-red-100 text-red-700 border border-red-200" title="Reject" data-testid={`reject-regimen-${r.id}`}><X className="w-4 h-4" /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-stone-200 rounded-xl shadow-soft overflow-hidden">
          <div className="px-5 py-4 border-b border-stone-100 flex items-center gap-2">
            <Pill className="w-4 h-4 text-brand-500" />
            <h2 className="font-display font-semibold text-stone-900">Drug requests <span className="text-stone-400 font-normal">({drugReqs.length})</span></h2>
          </div>
          <div className="divide-y divide-stone-100">
            {drugReqs.length === 0 && <div className="p-8 text-center text-stone-500 text-sm">All caught up.</div>}
            {drugReqs.map((r) => (
              <div key={r.id} className="px-5 py-4" data-testid={`drug-req-${r.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-semibold text-stone-900">{r.name}</div>
                    <div className="text-xs text-stone-500 mt-0.5">
                      Requested by {r.requested_by_name} • {new Date(r.created_at).toLocaleDateString()}
                    </div>
                    <div className="text-xs text-stone-500 mt-0.5">{r.default_route} · {r.default_location}</div>
                    {r.description && <div className="text-sm text-stone-600 mt-1.5">{r.description}</div>}
                  </div>
                  {isSuper && (
                    <div className="flex gap-2 shrink-0">
                      <button onClick={() => decide("drug", r.id, "approved")} className="p-2 rounded-lg bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200" data-testid={`approve-drug-${r.id}`}><Check className="w-4 h-4" /></button>
                      <button onClick={() => decide("drug", r.id, "denied")} className="p-2 rounded-lg bg-red-50 hover:bg-red-100 text-red-700 border border-red-200" data-testid={`deny-drug-${r.id}`}><X className="w-4 h-4" /></button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white border border-stone-200 rounded-xl shadow-soft overflow-hidden">
          <div className="px-5 py-4 border-b border-stone-100 flex items-center gap-2">
            <CalendarClock className="w-4 h-4 text-warmth-500" />
            <h2 className="font-display font-semibold text-stone-900">Reschedule requests <span className="text-stone-400 font-normal">({reschedReqs.length})</span></h2>
          </div>
          <div className="divide-y divide-stone-100">
            {reschedReqs.length === 0 && <div className="p-8 text-center text-stone-500 text-sm">No pending reschedule requests.</div>}
            {reschedReqs.map((r) => (
              <div key={r.id} className="px-5 py-4" data-testid={`resched-req-${r.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap mb-2">
                      <span className="font-semibold text-stone-900">Reschedule request</span>
                      {r._source === "patient_request" && (
                        <span className="text-[10px] uppercase tracking-wider font-bold text-white bg-indigo-500 px-2 py-0.5 rounded-full" title="Submitted via patient mobile app">Mobile</span>
                      )}
                      {r.event_type && (
                        <span className="text-[10px] uppercase tracking-wider font-bold text-stone-600 bg-stone-100 border border-stone-200 px-2 py-0.5 rounded-full">{r.event_type}</span>
                      )}
                    </div>

                    {/* Old → New date pill */}
                    <div className="inline-flex items-center gap-2 bg-stone-50 border border-stone-200 rounded-lg px-3 py-2 mb-2">
                      <div className="text-xs">
                        <div className="text-[10px] uppercase tracking-wider text-stone-500 font-bold">From</div>
                        <div className="font-mono font-semibold text-stone-900 text-sm">{r.old_date || "—"}</div>
                      </div>
                      <svg className="w-4 h-4 text-stone-400 mx-1" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path fillRule="evenodd" d="M10.293 3.293a1 1 0 0 1 1.414 0l6 6a1 1 0 0 1 0 1.414l-6 6a1 1 0 0 1-1.414-1.414L14.586 11H3a1 1 0 1 1 0-2h11.586l-4.293-4.293a1 1 0 0 1 0-1.414z" clipRule="evenodd"/></svg>
                      <div className="text-xs">
                        <div className="text-[10px] uppercase tracking-wider text-emerald-600 font-bold">To</div>
                        <div className="font-mono font-semibold text-emerald-700 text-sm">{r.new_date}</div>
                      </div>
                    </div>

                    {/* Appointment context */}
                    <div className="text-xs text-stone-600 space-y-0.5">
                      {(r.regimen_name || r.cycle_number) && (
                        <div>
                          <span className="text-stone-400">Regimen:</span>{" "}
                          <span className="font-medium text-stone-800">{r.regimen_name || "—"}</span>
                          {r.cycle_number && <> · <span className="text-stone-500">Cycle {r.cycle_number}</span></>}
                        </div>
                      )}
                      {r.event_label && (
                        <div>
                          <span className="text-stone-400">Appointment:</span>{" "}
                          <span className="font-medium text-stone-800">{r.event_label}</span>
                          {r.event_time && <span className="text-stone-500"> · {r.event_time}</span>}
                        </div>
                      )}
                      <div>
                        <span className="text-stone-400">Patient:</span>{" "}
                        <span className="font-medium text-stone-800">{r.patient_name || "Unknown"}</span>
                        <span className="text-stone-400 ml-1.5 font-mono text-[10px]">ID {(r.patient_id || "").slice(0, 8)}…</span>
                      </div>
                    </div>

                    {r.reason && (
                      <div className="mt-2 text-sm text-stone-700 bg-amber-50 border-l-2 border-amber-300 px-3 py-1.5 italic">
                        "{r.reason}"
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button onClick={() => decide("resched", r.id, "approved", r)} className="p-2 rounded-lg bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200" data-testid={`approve-resched-${r.id}`} title="Approve & apply new date"><Check className="w-4 h-4" /></button>
                    <button onClick={() => decide("resched", r.id, "denied", r)} className="p-2 rounded-lg bg-red-50 hover:bg-red-100 text-red-700 border border-red-200" data-testid={`deny-resched-${r.id}`} title="Deny"><X className="w-4 h-4" /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
